import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { FC, useEffect, useState } from "react";
import { DetailsUI, ProfileUI } from "../BoxUI";
import useResponsive from "../../hooks/useResponsive";
import { EmploymentDetailsProps } from "../../interface/employment";
import {
  formatDate,
  formatFullName,
  formatTextType,
  getUrlPathId,
  limitToSeven,
} from "../../utils/url-id";
import { EmploymentCommentModal, EmploymentUpdateModal } from "./statusModal";
import EmployeeFeedback from "./EmployeeFeedback";
import { useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { apiGetMobilityScoreByEmployeeId } from "../../api/dashboard";
import { useNotificationService } from "../notification";
import MobilityTable from "./MobilityTable";
import { useWalletSettings } from "../../hooks/useWalletSetting";
import EmployeeTimesheet from "./EmployeeTimeSheet";

const EmploymentDetailsContent: FC<{
  employee_info: EmploymentDetailsProps;
  loading: boolean;
  modalStatus: string;
  setModalStatus: React.Dispatch<React.SetStateAction<string>>;
}> = ({ employee_info, loading, modalStatus, setModalStatus }) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { token } = useAuth();
  const { showNotification } = useNotificationService();

  const { userBalance } = useWalletSettings(token);

  const handleModal = () => setOpen(!open);

  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = () => setOpenModal(!openModal);

  const handleRefresh = () => setRefresh(!refresh);
  const headerSty = {
    fontFamily: "Lexend",
    fontWeight: 500,
    my: isMedium ? 2 : 3,
    color: theme.palette.neutral.light,
  };
  useEffect(() => {
    const employeeId = getUrlPathId(location?.pathname);
    const fields = { employeeId, token };
    mutation.mutate(fields);
  }, []);

  const mutation = useMutation({
    mutationFn: (fields: { token: string; employeeId: string }) =>
      apiGetMobilityScoreByEmployeeId(fields),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        if (Array.isArray(data?.data?.data)) {
        } else {
        }
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });
  return (
    <>
      <EmploymentUpdateModal
        open={open}
        modalStatus={modalStatus}
        setModalStatus={setModalStatus}
        employeeId={
          employee_info?.employee_id ?? employee_info?._id.substring(0, 7)
        }
        employmentId={employee_info?._id}
        status={employee_info?.status}
        handleModal={handleModal}
      />
      <EmploymentCommentModal
        employeeId={employee_info?.employee_id ?? employee_info?._id}
        employmentId={employee_info?._id}
        open={openModal}
        modalHandler={handleOpenModal}
        handleRefresh={handleRefresh}
      />
      <Box
        sx={{
          margin: 3,
          padding: isMedium ? 3 : 4,
          borderRadius: "3px",
          minHeight: "100%",
          backgroundColor: theme.palette.common.white,
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 2 }}
        >
          <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
            Employee Profile
          </Typography>
          <Button
            variant="contained"
            onClick={handleModal}
            sx={{ fontWeight: 700 }}
          >
            Update status
          </Button>
        </Stack>

        <Box>
          {!loading ? (
            <ProfileUI
              name={formatFullName(
                employee_info?.medic_info?.first_name,
                employee_info?.medic_info?.last_name
              )}
              email={employee_info?.medic_info?.email ?? "-"}
              staffId={
                employee_info?.employee_id ?? limitToSeven(employee_info?._id)
              }
            />
          ) : (
            <Skeleton variant="rounded" width={"100%"} height={80} />
          )}
          <Typography sx={headerSty} variant="subtitle2">
            Employment Details
          </Typography>
          {!loading ? (
            <DetailsUI
              type={formatTextType(employee_info?.job_info?.employment_type)}
              title={formatTextType(employee_info?.job_info?.job_title)}
              department={employee_info?.job_info?.department ?? "-"}
              startDate={formatDate(
                employee_info?.employment_details?.start_date
              )}
              endDate={formatDate(employee_info?.employment_details?.end_date)}
            />
          ) : (
            <Skeleton variant="rounded" width={"100%"} height={120} />
          )}

          {/* <Typography sx={headerSty} variant="subtitle2">
            Overlapping Employment
          </Typography>
          <EmploymentInfoUI />
          <Stack direction={"row"} alignItems={"center"} sx={{ gap: 3, my: 4 }}>
            <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle2">
              Rehire Eligibility Indicator
            </Typography>
            <span
              style={{
                background: theme.palette.success.light,
                padding: "8px 14px",
                borderRadius: "4px",
              }}
            >
              <p
                style={{
                  color: theme.palette.success.main,
                  fontSize: "12px",
                  margin: 0,
                  fontFamily: "Inter",
                }}
              >
                Positive
              </p>
            </span>
          </Stack> */}
          {/* <Typography sx={{ ...headerSty, my: 2, mb: 2 }} variant="subtitle2">
            Employee Request
          </Typography> */}

          {/* <EmployeeRequestContent /> */}

          {/* <Typography sx={headerSty} variant="subtitle2">
            Historical Trend Analysis
          </Typography>

          <Box
            sx={{
              paddingY: 2,
              paddingX: isSmall ? 0 : 2,
              backgroundColor: "#FAFBFC",
              borderRadius: "6px",
              width: "100%",
              mb: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              color={"neutral"}
              sx={{
                fontWeight: 700,
                fontFamily: "Inter",
                letterSpacing: "3%",
                mb: 1,
                paddingX: 2,
              }}
            >
              Employee shift
            </Typography>
            <BarChart />
          </Box> */}
          {/* <Box
            sx={{
              paddingY: 2,
              paddingX: isSmall ? 0 : 2,
              backgroundColor: "#FAFBFC",
              borderRadius: "6px",
              width: "100%",
              mt: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              color={"neutral"}
              sx={{
                fontWeight: 700,
                fontFamily: "Inter",
                letterSpacing: "3%",
                mb: 1,
                paddingX: 2,
              }}
            >
              Employee shift
            </Typography>
            <LineChart />
          </Box> */}
        </Box>
        <Stack
          direction="row"
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography sx={headerSty} variant="subtitle2">
            Feedback and Comments
          </Typography>
          <Button
            variant="contained"
            sx={{
              fontWeight: 700,
              my: 3,
            }}
            onClick={handleOpenModal}
          >
            Add Comment
          </Button>
        </Stack>
        <EmployeeFeedback refresh={refresh} />

        <Typography sx={headerSty} variant="subtitle2">
          Mobility Score
        </Typography>

        {userBalance === 0 ? (
          <Typography
            variant="body1"
            sx={{ textAlign: "center", fontWeight: 600 }}
          >
            Kindly subscribe to access this feature
          </Typography>
        ) : (
          <MobilityTable medicId={employee_info?._id} />
        )}
        <>
          <Typography sx={headerSty} variant="subtitle2">
            Timesheet & Shifts
          </Typography>
          <EmployeeTimesheet employeeId={employee_info?.medic} />
        </>
      </Box>
    </>
  );
};

export default EmploymentDetailsContent;
